import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

export interface DataTableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  /** Right-aligned, tabular-numeral column — use for every money/quantity figure. */
  numeric?: boolean;
  widthClassName?: string;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  onActivate?: (row: T) => void;
  emptyMessage?: string;
  /** Selected row index is managed internally unless these are supplied. */
  selectedIndex?: number;
  onSelectedIndexChange?: (index: number) => void;
  className?: string;
}

/**
 * Dense data grid with sticky header and roving-row keyboard navigation:
 * Up/Down moves the highlighted row, Home/End jump to the ends, Enter
 * activates it (opens/edits). No mouse required.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onActivate,
  emptyMessage = 'Nothing here yet.',
  selectedIndex,
  onSelectedIndexChange,
  className,
}: DataTableProps<T>) {
  const [internalIndex, setInternalIndex] = useState(0);
  const index = selectedIndex ?? internalIndex;
  const setIndex = onSelectedIndexChange ?? setInternalIndex;
  const containerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());

  useEffect(() => {
    if (index >= rows.length && rows.length > 0) setIndex(rows.length - 1);
  }, [rows.length, index, setIndex]);

  useEffect(() => {
    rowRefs.current.get(index)?.scrollIntoView({ block: 'nearest' });
  }, [index]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (rows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex(Math.min(index + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex(Math.max(index - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setIndex(rows.length - 1);
    } else if (e.key === 'Enter') {
      const row = rows[index];
      if (row) onActivate?.(row);
    }
  }

  if (rows.length === 0) {
    return <div className="border border-[var(--border)] px-3 py-8 text-center text-[12.5px] text-[var(--text-faint)]">{emptyMessage}</div>;
  }

  return (
    <div
      ref={containerRef}
      role="grid"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={clsx('max-h-full overflow-auto border border-[var(--border)] outline-none focus-visible:ring-1 focus-visible:ring-[var(--focus-ring)]', className)}
    >
      <table className="w-full border-collapse text-[12.5px]">
        <thead className="sticky top-0 z-10 bg-[var(--bg-inset)]">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={clsx(
                  'border-b border-[var(--border-strong)] px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]',
                  col.numeric ? 'text-right' : 'text-left',
                  col.widthClassName,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={getRowKey(row)}
              ref={(el) => {
                if (el) rowRefs.current.set(i, el);
                else rowRefs.current.delete(i);
              }}
              onClick={() => setIndex(i)}
              onDoubleClick={() => onActivate?.(row)}
              aria-selected={i === index}
              className={clsx(
                'cursor-pointer border-b border-[var(--border)] last:border-b-0',
                i === index ? 'bg-[var(--bg-selected)]' : i % 2 === 1 ? 'bg-[var(--bg-inset)]/40' : undefined,
              )}
            >
              {columns.map((col) => (
                <td key={col.key} className={clsx('px-2 py-[3px]', col.numeric && 'num')}>
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
